import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { axiosInstance } from "../../../utils/axios";
import AdminSidebar from "../../../components/AdminSidebar";
import LoadingComponent from "../../../components/LoadingComponent";
import NoData from "../../../components/NoData";
import Button from "../../../components/Button";
import dayjs from "dayjs";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";

export default function RentalDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: rental, isLoading } = useQuery({
    queryKey: ["device-rental", id],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/device-rentals/${id}`
      );
      return response.data.data || response.data;
    },
  });

  if (isLoading) return <LoadingComponent />;
  if (!rental) return <NoData message="Rental not found" />;

  const getPaidThrough = () => {
    if (!rental.last_billing_period_start || !rental.last_billing_period_end)
      return "---";

    const start = dayjs(rental.last_billing_period_start);
    const end = dayjs(rental.last_billing_period_end);
    const months = [];

    let current = start.startOf("month");
    const last = end.startOf("month");

    while (current.isBefore(last) || current.isSame(last, "month")) {
      months.push(current.format("MMMM"));
      current = current.add(1, "month");
    }

    return months.join(", ");
  };

  const renderField = (label, value) => (
    <div className="flex flex-col">
      <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
        {label}
      </label>
      <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 font-medium">
        {value || "---"}
      </div>
    </div>
  );

  return (
    <AdminSidebar>
      <div className="px-4 sm:px-6 lg:px-8 bg-bgBlue min-h-screen pb-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center gap-4">
              <Button
                variant="secondary"
                size="sm"
                icon={<ArrowLeftIcon className="h-4 w-4" />}
                onClick={() => navigate(-1)}
              >
                Back
              </Button>
              <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
                Rental Details
              </h1>
            </div>
            <Button
              href={`/admin/rentals/${id}/edit`}
              variant="primary"
              size="md"
            >
              Edit Rental
            </Button>
          </div>

          <div className="space-y-6">
            {/* Client Information Section */}
            <div className="bg-white shadow-sm rounded-xl overflow-hidden border border-gray-100">
              <div className="bg-gray-50 px-6 py-3 border-b border-gray-100">
                <h2 className="text-sm font-bold text-gray-700 uppercase">
                  Client Information
                </h2>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {renderField("Client ID", rental.client_id)}
                {renderField("Client Name", rental.client?.name)}
                {renderField("Location", rental.location)}
                {renderField("Email", rental.email)}
                {renderField("Phone", rental.phone)}
              </div>
            </div>

            {/* Device & Status Section */}
            <div className="bg-white shadow-sm rounded-xl overflow-hidden border border-gray-100">
              <div className="bg-gray-50 px-6 py-3 border-b border-gray-100">
                <h2 className="text-sm font-bold text-gray-700 uppercase">
                  Device & Status
                </h2>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {renderField("Device Model", rental.device_model?.name)}
                {renderField("Serial Number", rental.serial_number)}
                {renderField("Status", rental.status)}
                {renderField(
                  "Last Counter Update",
                  rental.last_date_of_counter_activation
                    ? dayjs(rental.last_date_of_counter_activation).format(
                        "DD/MM/YYYY"
                      )
                    : null
                )}
              </div>
            </div>

            {/* Billing & Pricing Section */}
            <div className="bg-white shadow-sm rounded-xl overflow-hidden border border-gray-100">
              <div className="bg-gray-50 px-6 py-3 border-b border-gray-100">
                <h2 className="text-sm font-bold text-gray-700 uppercase">
                  Billing & Pricing
                </h2>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {renderField("Monthly Rental Price", rental.monthly_rental_price)}
                {renderField("B/W Price", rental.black_white_price)}
                {renderField("Color Price", rental.color_price)}
                {renderField("Previous B/W Counter", rental.last_billed_black_white_counter)}
                {renderField("Previous Color Counter", rental.last_billed_color_counter)}
                {renderField("Current B/W Counter", rental.black_white_counter)}
                {renderField("Current Color Counter", rental.color_counter)}
                {renderField("Paid Through", getPaidThrough())}
                {renderField(
                  "Billing Cycle End",
                  rental.next_billing
                    ? dayjs(rental.next_billing).format("YYYY-MM")
                    : null
                )}
              </div>
            </div>

            {/* Contract Dates Section */}
            <div className="bg-white shadow-sm rounded-xl overflow-hidden border border-gray-100">
              <div className="bg-gray-50 px-6 py-3 border-b border-gray-100">
                <h2 className="text-sm font-bold text-gray-700 uppercase">
                  Contract Dates
                </h2>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {renderField(
                  "Contract Start Date",
                  rental.contract_start_date
                    ? dayjs(rental.contract_start_date).format("DD/MM/YYYY")
                    : null
                )}
                {renderField(
                  "Expected End Date",
                  rental.expected_end_date
                    ? dayjs(rental.expected_end_date).format("DD/MM/YYYY")
                    : null
                )}
                {renderField(
                  "Actual End Date",
                  rental.actual_end_date
                    ? dayjs(rental.actual_end_date).format("DD/MM/YYYY")
                    : null
                )}
                {renderField(
                  "Created At",
                  rental.created_at
                    ? dayjs(rental.created_at).format("DD/MM/YYYY HH:mm")
                    : null
                )}
                {renderField(
                  "Updated At",
                  rental.updated_at
                    ? dayjs(rental.updated_at).format("DD/MM/YYYY HH:mm")
                    : null
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminSidebar>
  );
}